import { useEffect, useState } from 'react'
import { supabase } from '../supabaseClient'

const ORANGE = '#FF5722'
const BG = '#0D0D0D'
const CARD = '#141414'
const BORDER = '#1f1f1f'

const BANGALORE_AREAS = [
  'Indiranagar',
  'Koramangala',
  'HSR Layout',
  'Jayanagar',
  'JP Nagar',
  'BTM Layout',
  'Whitefield',
  'Marathahalli',
  'Bellandur',
  'Sarjapur Road',
  'Electronic City',
  'Yelahanka',
  'Malleshwaram',
  'Rajajinagar',
  'Banashankari',
  'Hebbal',
  'Basavanagudi',
  'Ulsoor',
  'Frazer Town',
  'MG Road'
]

const QUESTION_FIELDS = [
  { key: 'biryani_allegiance', label: 'Biryani allegiance' },
  { key: 'hidden_gem', label: 'Hidden gem in Bangalore' },
  { key: 'comfort_order', label: 'Comfort order' },
  { key: 'staple_place', label: 'Staple place you keep going back to' },
  { key: 'food_confession', label: 'Food confession' }
]

function BottomIcon({ id, active }) {
  const color = active ? ORANGE : '#444'

  if (id === 'home') {
    return active ? (
      <svg width="24" height="24" viewBox="0 0 24 24"><path fill={color} d="M12.2 3.5c.3 2.8-.8 4.4-2 5.7-1.1 1.2-2.2 2.3-2.2 4.1a4.1 4.1 0 008.2 0c0-2.9-1.8-4.7-4-9.8z" /></svg>
    ) : (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none"><path d="M12.2 3.5c.3 2.8-.8 4.4-2 5.7-1.1 1.2-2.2 2.3-2.2 4.1a4.1 4.1 0 008.2 0c0-2.9-1.8-4.7-4-9.8z" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
    )
  }
  if (id === 'discover') {
    return active ? (
      <svg width="24" height="24" viewBox="0 0 24 24"><path fill={color} d="M7.2 4.5c.8 0 1.4.6 1.4 1.4v3.9l3.6 3.6 1.9-1.9-.9-.9a1.2 1.2 0 011.7-1.7l2.7 2.7a1.2 1.2 0 01-1.7 1.7l-.9-.9-1.9 1.9 3.6 3.6c.5.5.5 1.3 0 1.8l-.8.8-4.4-4.4-4.4 4.4-.8-.8a1.3 1.3 0 010-1.8l3.6-3.6-3.3-3.3V5.9c0-.8.6-1.4 1.4-1.4z" /></svg>
    ) : (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none"><path d="M8.2 4.5v5.8l3.8 3.8M5.9 4.5h4.6M6.8 7h2.8M13.2 9.6l4 4M16.8 8.4l1.8 1.8M15.6 10.8L9 17.4M8.6 19.8L19.8 8.6" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /></svg>
    )
  }
  if (id === 'matches') {
    return active ? (
      <svg width="24" height="24" viewBox="0 0 24 24"><path fill={color} d="M6 5.5h12a3 3 0 013 3v6a3 3 0 01-3 3h-7.1L6.5 21v-3.5H6a3 3 0 01-3-3v-6a3 3 0 013-3z" /></svg>
    ) : (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none"><path d="M6 5.5h12a3 3 0 013 3v6a3 3 0 01-3 3h-7.1L6.5 21v-3.5H6a3 3 0 01-3-3v-6a3 3 0 013-3z" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
    )
  }
  return active ? (
    <svg width="24" height="24" viewBox="0 0 24 24"><circle cx="12" cy="8" r="4" fill={color} /><path fill={color} d="M4 21a8 8 0 0116 0H4z" /></svg>
  ) : (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none"><path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /><circle cx="12" cy="7" r="4" stroke={color} strokeWidth="2" /></svg>
  )
}

export default function Profile({ session, onNavigate }) {
  const [form, setForm] = useState({
    full_name: '',
    age: '',
    area: '',
    biryani_allegiance: '',
    hidden_gem: '',
    comfort_order: '',
    staple_place: '',
    food_confession: ''
  })
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')

  useEffect(() => {
    loadProfile()
  }, []) // eslint-disable-line

  async function loadProfile() {
    setLoading(true)
    setError('')

    const [{ data: userData, error: userError }, { data: questionData, error: questionError }] =
      await Promise.all([
        supabase
          .from('users')
          .select('full_name, age, area')
          .eq('id', session.user.id)
          .maybeSingle(),
        supabase
          .from('profile_questions')
          .select('biryani_allegiance, hidden_gem, comfort_order, staple_place, food_confession')
          .eq('user_id', session.user.id)
          .maybeSingle()
      ])

    if (userError || questionError) {
      setError(userError?.message || questionError?.message || 'Could not load profile.')
      setLoading(false)
      return
    }

    setForm(prev => ({
      ...prev,
      full_name: userData?.full_name || '',
      age: userData?.age ? String(userData.age) : '',
      area: userData?.area || '',
      biryani_allegiance: questionData?.biryani_allegiance || '',
      hidden_gem: questionData?.hidden_gem || '',
      comfort_order: questionData?.comfort_order || '',
      staple_place: questionData?.staple_place || '',
      food_confession: questionData?.food_confession || ''
    }))

    setLoading(false)
  }

  function updateField(key, value) {
    setForm(prev => ({ ...prev, [key]: value }))
  }

  async function saveProfile() {
    setSaving(true)
    setError('')
    setSuccess('')

    const parsedAge = Number(form.age)
    if (form.age && (!Number.isInteger(parsedAge) || parsedAge < 18 || parsedAge > 100)) {
      setError('Age must be a whole number between 18 and 100.')
      setSaving(false)
      return
    }

    const { error: userError } = await supabase
      .from('users')
      .upsert({
        id: session.user.id,
        full_name: form.full_name.trim(),
        age: form.age ? parsedAge : null,
        area: form.area || null
      }, { onConflict: 'id' })

    if (userError) {
      setError(userError.message)
      setSaving(false)
      return
    }

    const { error: questionError } = await supabase
      .from('profile_questions')
      .upsert({
        user_id: session.user.id,
        biryani_allegiance: form.biryani_allegiance.trim(),
        hidden_gem: form.hidden_gem.trim(),
        comfort_order: form.comfort_order.trim(),
        staple_place: form.staple_place.trim(),
        food_confession: form.food_confession.trim()
      }, { onConflict: 'user_id' })

    if (questionError) {
      setError(questionError.message)
      setSaving(false)
      return
    }

    setSuccess('Profile saved.')
    setSaving(false)
  }

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        background: BG,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#666',
        fontFamily: "'DM Sans', sans-serif",
        fontSize: 14
      }}>
        Loading profile...
      </div>
    )
  }

  return (
    <div style={{
      minHeight: '100vh',
      background: BG,
      color: '#fff',
      fontFamily: "'DM Sans', sans-serif",
      paddingBottom: 92
    }}>
      <div style={{
        padding: '52px 20px 20px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        borderBottom: `1.5px solid ${BORDER}`
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <div
            onClick={() => onNavigate('home')}
            style={{
              width: 36,
              height: 36,
              borderRadius: '50%',
              background: CARD,
              border: `1.5px solid ${BORDER}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              fontSize: 16
            }}
          >
            ←
          </div>
          <div>
            <div style={{ fontSize: 22, fontWeight: 800 }}>
              Your profile
            </div>
            <div style={{ fontSize: 12, color: '#666', marginTop: 2 }}>
              {session.user.email}
            </div>
          </div>
        </div>
        <div
          onClick={() => onNavigate('signout')}
          style={{
            fontSize: 12,
            color: ORANGE,
            cursor: 'pointer',
            fontWeight: 600
          }}
        >
          Sign out
        </div>
      </div>

      <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div style={{
          background: CARD,
          border: `1.5px solid ${BORDER}`,
          borderRadius: 18,
          padding: 16
        }}>
          <div style={{ fontSize: 11, color: '#666', letterSpacing: 1.1, textTransform: 'uppercase', fontWeight: 700, marginBottom: 14 }}>
            Basics
          </div>

          <label style={{ display: 'block', marginBottom: 12 }}>
            <div style={{ fontSize: 13, color: '#ccc', marginBottom: 8 }}>Full name</div>
            <input
              type="text"
              value={form.full_name}
              onChange={e => updateField('full_name', e.target.value)}
              placeholder="What should people call you?"
              style={inputStyle}
            />
          </label>

          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <label style={{ flex: '1 1 110px' }}>
              <div style={{ fontSize: 13, color: '#ccc', marginBottom: 8 }}>Age</div>
              <input
                type="number"
                min="18"
                max="100"
                value={form.age}
                onChange={e => updateField('age', e.target.value)}
                placeholder="24"
                style={inputStyle}
              />
            </label>

            <label style={{ flex: '2 1 220px' }}>
              <div style={{ fontSize: 13, color: '#ccc', marginBottom: 8 }}>Area</div>
              <select
                value={form.area}
                onChange={e => updateField('area', e.target.value)}
                style={inputStyle}
              >
                <option value="">Select your area</option>
                {BANGALORE_AREAS.map(area => (
                  <option key={area} value={area}>{area}</option>
                ))}
              </select>
            </label>
          </div>
        </div>

        <div style={{
          background: CARD,
          border: `1.5px solid ${BORDER}`,
          borderRadius: 18,
          padding: 16
        }}>
          <div style={{ fontSize: 11, color: '#666', letterSpacing: 1.1, textTransform: 'uppercase', fontWeight: 700, marginBottom: 14 }}>
            Food questions
          </div>

          {QUESTION_FIELDS.map((question, index) => (
            <label key={question.key} style={{ display: 'block', marginBottom: index === QUESTION_FIELDS.length - 1 ? 0 : 12 }}>
              <div style={{ fontSize: 13, color: '#ccc', marginBottom: 8 }}>{question.label}</div>
              <textarea
                value={form[question.key]}
                onChange={e => updateField(question.key, e.target.value)}
                rows={2}
                placeholder="Type your answer..."
                style={{
                  ...inputStyle,
                  resize: 'vertical',
                  minHeight: 72
                }}
              />
            </label>
          ))}
        </div>

        {error ? <div style={{ color: '#ff6b6b', fontSize: 13 }}>{error}</div> : null}
        {success ? <div style={{ color: '#66d17a', fontSize: 13 }}>{success}</div> : null}

        <button
          onClick={saveProfile}
          disabled={saving}
          style={{
            marginTop: 4,
            background: ORANGE,
            color: '#fff',
            border: 'none',
            borderRadius: 12,
            padding: '14px 16px',
            fontSize: 15,
            fontWeight: 700,
            fontFamily: "'DM Sans', sans-serif",
            cursor: saving ? 'not-allowed' : 'pointer',
            opacity: saving ? 0.75 : 1
          }}
        >
          {saving ? 'Saving...' : 'Save profile'}
        </button>
      </div>

      <div style={{ position: 'fixed', bottom: 0, left: '50%', transform: 'translateX(-50%)', width: '100%', maxWidth: '100%', background: '#0a0a0a', borderTop: `1.5px solid ${BORDER}`, display: 'flex', padding: '10px 0 20px', zIndex: 100 }}>
        {[
          { label: 'Home', id: 'home' },
          { label: 'Discover', id: 'discover' },
          { label: 'Matches', id: 'matches' },
          { label: 'Profile', id: 'profile' },
        ].map(nav => (
          <div key={nav.id} onClick={() => onNavigate(nav.id)} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, cursor: 'pointer', padding: '4px 0' }}>
            <BottomIcon id={nav.id} active={nav.id === 'profile'} />
            <span style={{ fontSize: 10, fontWeight: 500, color: nav.id === 'profile' ? ORANGE : '#444', letterSpacing: 0.3 }}>{nav.label}</span>
          </div>
        ))}
      </div>
    </div>
  )
}

const inputStyle = {
  width: '100%',
  background: '#101010',
  border: `1.5px solid ${BORDER}`,
  borderRadius: 12,
  color: '#fff',
  padding: '11px 12px',
  fontSize: 14,
  outline: 'none',
  fontFamily: "'DM Sans', sans-serif",
  boxSizing: 'border-box'
}
